import React from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';

import { FontSize } from '@anyx/common-graphql';
import {
  ImageLoadable,
  LinkHighlighter,
  PreviewSlider,
  PreviewSliderHandle,
  UploadedImageFile,
} from '@anyx/feature-crm/shared';

import { AlignEnum, StyledTextInputComponent } from '../line-message-form';

export interface LineButtonTypeBubbleProps {
  id: string;
  cards?: ButtonTypeCardProps[];
  className?: string;
  classes?: {
    slider?: string;
    slides?: string;
  };
}

export interface ButtonTypeCardProps {
  image?: UploadedImageFile;
  title: StyledTextInputComponent;
  description: StyledTextInputComponent;
  actions: {
    label: string;
    url: string;
  }[];
  isImageBelowDescription: boolean;
}

const fontSizeClass = (size: FontSize) => {
  return classNames({
    'text-[14px]': size === 'sm',
    'text-[16px]': size === 'md',
    'text-[19px]': size === 'lg',
    'text-[22px]': size === 'xl',
    'text-[28px]': size === 'xxl',
  });
};

const alignClass = (align: AlignEnum) => {
  return classNames({
    'text-left': align === 'start',
    'text-center': align === 'center',
    'text-right': align === 'end',
  });
};

const fontWeightClass = (isBold: boolean) => {
  return classNames({
    'font-bold': isBold,
    'font-normal': !isBold,
  });
};

const ButtonTypeImage = ({
  image,
  isPlaceholder,
}: {
  image?: UploadedImageFile;
  isPlaceholder?: boolean;
}) => {
  if (!isPlaceholder && !image) {
    return null;
  }

  return <ImageLoadable className="aspect-[1.51/1]" src={image?.src} alt={image?.name} />;
};

const ButtonTypeText = ({
  text,
  fontSize,
  weighted,
  placeholder,
  isPlaceholder,
  align,
}: {
  text: string;
  fontSize: FontSize;
  weighted: boolean;
  align: AlignEnum;
  placeholder?: string;
  isPlaceholder?: boolean;
}) => {
  if (!isPlaceholder && !text) {
    return null;
  }

  return (
    <LinkHighlighter
      className={classNames(
        'break-words',
        {
          'text-[14px]': fontSize === 'sm',
          'text-[16px]': fontSize === 'md',
          'text-[19px]': fontSize === 'lg',
          'text-[22px]': fontSize === 'xl',
          'text-[28px]': fontSize === 'xxl',
          'text-gray-9': text,
          'text-gray-5': !text,
        },
        fontSizeClass(fontSize),
        fontWeightClass(weighted),
        alignClass(align)
      )}
      text={text}
      placeholder={placeholder}
    />
  );
};

const ButtonTypeActions = ({
  actions,
  isPlaceholder,
}: {
  actions: {
    label: string;
    url: string;
  }[];
  isPlaceholder?: boolean;
}) => {
  const { t } = useTranslation('crm');
  if (!isPlaceholder && actions.length === 0) {
    return null;
  }

  const actionItems =
    actions.length === 0
      ? [
          {
            label: t('crm.lineMessageForm.fields.actionLabel.placeholder'),
            url: '',
          },
        ]
      : actions;

  return (
    <>
      {actionItems.map((item, idx) => (
        <a
          key={idx}
          href={item.url}
          target="_blank"
          rel="noreferrer"
          className={`${classNames({
            'text-gray-5': isPlaceholder,
            'text-[#80A0D3]': !isPlaceholder,
          })} flex h-8 items-center justify-center leading-none`}
        >
          {item.label || t('crm.lineMessageForm.fields.actionLabel.placeholder')}
        </a>
      ))}
    </>
  );
};

const ButtonTypePreviewCard = ({
  image,
  title,
  description,
  actions,
  isPlaceholder,
  isImageBelowDescription,
}: ButtonTypeCardProps & {
  isPlaceholder?: boolean;
}) => {
  const { t } = useTranslation('crm');
  return (
    <div
      className={`my-1 flex w-full flex-col gap-y-6 overflow-hidden rounded-2xl shadow ${classNames(
        {
          'bg-white': !isPlaceholder,
        }
      )}`}
    >
      {!isImageBelowDescription && (isPlaceholder || image) && (
        <ButtonTypeImage isPlaceholder image={image} />
      )}

      {(isPlaceholder || title.text || description.text) && (
        <div className="mx-4 flex flex-col gap-y-2 first:pt-6 last:pb-6">
          <ButtonTypeText
            {...title}
            placeholder={t('crm.lineMessageForm.fields.title.placeholder')}
            isPlaceholder={isPlaceholder}
          />
          <ButtonTypeText
            {...description}
            placeholder={t('crm.lineMessageForm.fields.description.placeholder')}
            isPlaceholder={isPlaceholder}
          />
        </div>
      )}

      {isImageBelowDescription && (isPlaceholder || image) && (
        <ButtonTypeImage isPlaceholder image={image} />
      )}

      {(isPlaceholder || actions.length > 0) && (
        <div className="mx-4 flex flex-col gap-y-2 first:pt-6 last:pb-6">
          <ButtonTypeActions actions={actions} isPlaceholder={isPlaceholder} />
        </div>
      )}
    </div>
  );
};

export const LineButtonTypeBubble = React.forwardRef<
  PreviewSliderHandle,
  LineButtonTypeBubbleProps
>(({ id, cards = [], className, classes }, ref) => {
  return (
    <PreviewSlider
      ref={ref}
      id={id}
      className={className}
      classes={{
        slider: classes?.slider,
        slides: classNames('justify-start!', classes?.slides),
      }}
    >
      {cards.map(({ image, title, description, actions, isImageBelowDescription }, i) => {
        return (
          <ButtonTypePreviewCard
            key={i}
            image={image}
            title={title}
            description={description}
            actions={actions}
            isPlaceholder={!image && !title.text && !description.text && !actions?.[0]?.label}
            isImageBelowDescription={isImageBelowDescription}
          />
        );
      })}
    </PreviewSlider>
  );
});
